import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { LanguageContext } from "../../context/LanguageContext";
import {
  Alert,
  Box,
  Button,
  Grid,
  Typography,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LeftMenu from "../menus/LeftMenu";
import HeaderBar from "../headers/HeaderBar";
import AgentCard from "./AgentCard";
import AgentCreateWizardDesktop from "./AgentCreateWizardDesktop";
import ConfirmDeleteDialog from "../common/ConfirmDeletDialog";
import AgentSourcesDialog from "./AgentSourcesDialog";
import AgentTestDialog from "./AgentTestDialog";
import AgentIntegrationsDialog from "./AgentIntegrationsDialog";
import AgentConversationsDialog from "./AgentConversationsDialog";
import { useIsPremium } from "../../hooks/useIsPremium";
import { PLAN_LIMITS } from "../../constants/planLimits";

export default function AgentsDashboardDesktop() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { translate } = useContext(LanguageContext);
  const { isPremium } = useIsPremium();
  const planLimits = PLAN_LIMITS[isPremium ? "premium" : "free"];

  const [tenantId, setTenantId] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [filterStatus, setFilterStatus] = useState("active"); // active | inactive | all

  const [openCreate, setOpenCreate] = useState(false);

  const [agentToDelete, setAgentToDelete] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [sourcesAgent, setSourcesAgent] = useState(null);
  const [testAgent, setTestAgent] = useState(null);
  const [integrationsAgent, setIntegrationsAgent] = useState(null);
  const [conversationsAgent, setConversationsAgent] = useState(null);

  // 🔹 Récup tenantId
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      const p = await getDoc(doc(db, "profilsUsers", currentUser.uid));
      if (p.exists()) setTenantId(p.data().tenantId || null);
    })();
  }, [currentUser]);

  // 🔹 Flux agents
  useEffect(() => {
    if (!tenantId) return;
    const ref = collection(db, "tenants", tenantId, "agents");
    setLoadingAgents(true);
    const unsub = onSnapshot(ref, (snap) => {
      setAgents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoadingAgents(false);
    });
    return unsub;
  }, [tenantId]);

  // 🔹 Gestion suppression (ouvre modale)
  const askDelete = (agent) => {
    setAgentToDelete(agent);
    setConfirmOpen(true);
  };

  // 🔹 Confirmation suppression
  const confirmDelete = async () => {
    if (!tenantId || !agentToDelete) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "tenants", tenantId, "agents", agentToDelete.id));
      setConfirmOpen(false);
      setAgentToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const canCreateAgent = agents.length < planLimits.maxAgents;

  return (
    <Box sx={{ display: "flex", width: "100%", minHeight: "100vh" }}>
      <LeftMenu />
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <HeaderBar
          title={translate("Agents")}
          subtitle={translate("ManageAndMonitorAgents")}
          showProjectSelect={false}
          extraContent={
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="agent-filter-label">
                {translate("Filter") || "Filter"}
              </InputLabel>
              <Select
                labelId="agent-filter-label"
                label={translate("Filter") || "Filter"}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="active">{translate("Active") || "Active"}</MenuItem>
                <MenuItem value="inactive">
                  {translate("Inactive") || "Inactive"}
                </MenuItem>
                <MenuItem value="all">{translate("All") || "All"}</MenuItem>
              </Select>
            </FormControl>
          }
        />

        {!canCreateAgent && (
          <Alert
            severity="info"
            sx={{ mt: 2, mb: agents.length === 0 ? 1 : 2 }}
            action={
              !isPremium && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate("/app/premium")}
                >
                  {translate("PlanUpgradeCTA") || "Go Premium"}
                </Button>
              )
            }
          >
            {translate("AgentsLimitReached", {
              max: planLimits.maxAgents,
            })}
          </Alert>
        )}

        {agents.length === 0 && !loadingAgents && (
          <Typography sx={{ mt: 2, mb: 1, textAlign: "left" }}>
            {translate("NoAgentsYet")}
          </Typography>
        )}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {loadingAgents &&
            Array.from({ length: 6 }).map((_, idx) => (
              <Grid key={`skeleton-${idx}`} item xs={12} sm={6} md={4} lg={3}>
                <Skeleton
                  variant="rounded"
                  animation="wave"
                  width={160}
                  height={160}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
            ))}
          {agents
            .filter((a) => {
              if (filterStatus === "all") return true;
              if (filterStatus === "active") return a?.publicEnabled !== false;
              return a?.publicEnabled === false;
            })
            .map((agent) => (
            <Grid key={agent.id} item xs={12} sm={6} md={4} lg={3}>
              <AgentCard
                agent={agent}
                tenantId={tenantId}
                onEdit={(a) => navigate(`/app/agents/${a.id}`)}
                deleting={deleting && agentToDelete?.id === agent.id}
              />
            </Grid>
          ))}
          {/* Add Agent tile positioned after the last card */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() =>
                canCreateAgent ? setOpenCreate(true) : navigate("/app/premium")
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (canCreateAgent) {
                    setOpenCreate(true);
                  } else {
                    navigate("/app/premium");
                  }
                }
              }}
              sx={{
                width: 160,
                height: 160,
                borderRadius: 3,
                bgcolor: canCreateAgent ? "#003B2E" : "rgba(15,23,42,0.06)",
                border: canCreateAgent ? "1px solid #00463A" : "1px dashed #94a3b8",
                color: canCreateAgent ? "#00E676" : "#94a3b8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
                cursor: "pointer",
                transition:
                  "transform .15s ease, box-shadow .15s ease, filter .15s ease",
                boxShadow: 1,
                "&:hover": canCreateAgent
                  ? { filter: "brightness(1.08)", boxShadow: 3 }
                  : {},
                "&:active": { transform: "scale(0.98)" },
              }}
            >
              <AddIcon
                sx={{
                  fontSize: 40,
                  color: canCreateAgent ? "#00E676" : "#94a3b8",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: canCreateAgent ? "#00E676" : "#94a3b8",
                }}
              >
                {canCreateAgent
                  ? translate("AddAgent") || "Add agent"
                  : translate("PlanUpgradeCTA") || "Go Premium"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* CREATE (desktop wizard) */}
        <AgentCreateWizardDesktop
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          tenantId={tenantId}
        />

        {/* EDIT moved to full page via navigate */}

        {/* CONFIRM DELETE */}
        <ConfirmDeleteDialog
          open={confirmOpen}
          onCancel={() => !deleting && setConfirmOpen(false)}
          onConfirm={confirmDelete}
          loading={deleting}
          title={translate("DeleteAgentTitle") || "Delete agent"}
          description={
            translate("DeleteAgentDesc") ||
            "Are you sure you want to delete this agent? This action cannot be undone."
          }
          confirmLabel={translate("Delete") || "Delete"}
          cancelLabel={translate("Cancel") || "Cancel"}
        />
        {/* SOURCES */}
        <AgentSourcesDialog
          open={Boolean(sourcesAgent)}
          onClose={() => setSourcesAgent(null)}
          tenantId={tenantId}
          agentId={sourcesAgent?.id}
        />

        <AgentTestDialog
          open={Boolean(testAgent)}
          onClose={() => setTestAgent(null)}
          agent={testAgent}
          tenantId={tenantId}
        />

        <AgentConversationsDialog
          open={Boolean(conversationsAgent)}
          onClose={() => setConversationsAgent(null)}
          agent={conversationsAgent}
        />

        {/* INTEGRATIONS */}
        <AgentIntegrationsDialog
          open={Boolean(integrationsAgent)}
          onClose={() => setIntegrationsAgent(null)}
          agent={integrationsAgent}
          tenantId={tenantId}
        />
      </Box>
    </Box>
  );
}
